import { Pause, PlayArrow, VolumeUp } from '@mui/icons-material';
import { Box, IconButton, Slider, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import React from 'react';
import { formatTime } from '../../util/time';

export interface ClipTransportBarProps {
  /** Whether audio is currently intended to be playing. */
  playIntent: boolean;
  /** Current playback position, in seconds. */
  position: number;
  /** Total duration of the loaded clip, in seconds. */
  duration: number;
  /** Volume, 0–100. */
  volume: number;
  onTogglePlay: () => void;
  onSeek: (seconds: number) => void;
  onVolumeChange: (volume: number) => void;
  /** True when nothing is loaded in the shared player; controls are inert. */
  disabled?: boolean;
}

const bar: SxProps<Theme> = (theme: Theme) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 1.5,
  px: 2,
  py: 1.5,
  borderTop: `1px solid ${theme.palette.divider}`,
});

const timeLabel: SxProps<Theme> = {
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
  minWidth: 36,
  textAlign: 'center',
};

/**
 * A compact transport (play/pause, seek, time, volume) that mirrors the global
 * audio player. It is fully controlled — the consumer wires it to the shared
 * player state so both surfaces read and drive the same playback.
 */
export const ClipTransportBar: React.FC<ClipTransportBarProps> = ({
  playIntent,
  position,
  duration,
  volume,
  onTogglePlay,
  onSeek,
  onVolumeChange,
  disabled = false,
}) => (
  <Box sx={bar}>
    <IconButton
      onClick={onTogglePlay}
      disabled={disabled}
      aria-label={playIntent ? 'Pause' : 'Play'}
      sx={{ color: 'text.primary' }}
    >
      {playIntent ? <Pause /> : <PlayArrow />}
    </IconButton>

    <Typography variant="caption" color="text.secondary" sx={timeLabel}>
      {formatTime(position * 1000)}
    </Typography>

    <Slider
      aria-label="Seek"
      value={Math.min(position, duration || 0)}
      max={duration || 0}
      disabled={disabled}
      onChange={(_, value) => onSeek(value as number)}
      sx={{ flex: 1 }}
    />

    <Typography variant="caption" color="text.secondary" sx={timeLabel}>
      {formatTime(duration * 1000)}
    </Typography>

    <VolumeUp fontSize="small" sx={{ color: 'text.secondary', ml: 0.5 }} />
    <Slider
      aria-label="Volume"
      value={volume}
      max={100}
      disabled={disabled}
      onChange={(_, value) => onVolumeChange(value as number)}
      sx={{ width: 96 }}
    />
  </Box>
);
